import { useState } from "react";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isSameDay,
  isAfter,
  isBefore,
  isToday,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { VscClose } from "react-icons/vsc";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const flexOptions = [
  { label: "Exact dates", value: "exact" },
  { label: "± 1 day", value: "1" },
  { label: "± 2 days", value: "2" },
  { label: "± 3 days", value: "3" },
  { label: "± 7 days", value: "7" },
  { label: "± 14 days", value: "14" },
];

function getCalendarDays(month) {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  const days = [];
  let current = start;

  while (current <= end) {
    days.push(current);
    current = addDays(current, 1);
  }

  return days;
}

function MonthGrid({ month, rangeStart, rangeEnd, onDayClick }) {
  const days = getCalendarDays(month);

  const isInRange = (day) => {
    if (!rangeStart || !rangeEnd) return false;
    return isAfter(day, rangeStart) && isBefore(day, rangeEnd);
  };

  const isStart = (day) => rangeStart && isSameDay(day, rangeStart);
  const isEnd = (day) => rangeEnd && isSameDay(day, rangeEnd);

  return (
    <div className="flex-1 min-w-[280px]">
      <h3 className="text-center font-semibold text-sm mb-3">
        {format(month, "MMMM yyyy")}
      </h3>

      <div className="grid grid-cols-7 gap-y-1">
        {WEEKDAYS.map((d, i) => (
          <div
            key={i}
            className="text-center text-xs font-medium text-gray-400 py-2"
          >
            {d}
          </div>
        ))}

        {days.map((day, i) => {
          const inMonth = isSameMonth(day, month);
          const selected = isStart(day) || isEnd(day);
          const inRange = isInRange(day);
          const past = isBefore(day, new Date()) && !isToday(day);

          return (
            <button
              key={i}
              disabled={!inMonth || past}
              onClick={() => inMonth && !past && onDayClick(day)}
              className={`
                relative h-10 w-full text-sm rounded-full transition-colors
                ${!inMonth ? "invisible" : ""}
                ${past && inMonth ? "text-gray-400 cursor-default" : ""}
                ${!past && inMonth && !selected && !inRange ? "hover:bg-[#ffefc9]/80" : ""}
                ${selected ? "bg-primary text-white font-semibold" : ""}
                ${inRange && !selected ? "bg-[#ffefc9]/80 rounded-none" : ""}
                ${isStart(day) && rangeEnd ? "rounded-l-full rounded-r-none" : ""}
                ${isEnd(day) && rangeStart ? "rounded-r-full rounded-l-none" : ""}
              `}
            >
              {inMonth ? format(day, "d") : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DateRangePicker({ range, onRangeSelect, onClose }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeTab, setActiveTab] = useState("dates");
  const [flexOption, setFlexOption] = useState("exact");
  const [rangeStart, setRangeStart] = useState(range?.from||null);
  const [rangeEnd, setRangeEnd] = useState(range?.to||null);

  const nextMonth = addMonths(currentMonth, 1);

  const handleDayClick = (day) => {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(day);
      setRangeEnd(null);

      if (onRangeSelect) {
        onRangeSelect({ from: day, to: null });
      }
    } else {
      if (isBefore(day, rangeStart)) {
        setRangeStart(day);
        setRangeEnd(rangeStart);

        if (onRangeSelect) {
          onRangeSelect({ from: day, to: rangeStart });
        }
      } else {
        setRangeEnd(day);

        if (onRangeSelect) {
          onRangeSelect({ from: rangeStart, to: day });
        }
      }
    }
  };


  const tabs = [
    { label: "Dates", value: "dates" },
    { label: "Flexible", value: "flexible" },
  ];

  return (
    <div className="bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-xl border p-8 w-full md:max-w-screen-md ">
      {/* Tabs */}
      <div className=" flex justify-end items-end">
        <VscClose fontSize={24} className="cursor-pointer" onClick={onClose} />
      </div>
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-200 rounded-full p-1">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`
                px-5 py-2 text-sm font-medium rounded-full transition-all
                ${
                  activeTab === tab.value
                    ? "bg-primary text-white shadow-sm"
                    : "text-black"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <>
        {/* Checkin / Checkout */}
        <div className="flex justify-center gap-8 mb-6">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase">Check-in</p>
            <p className="text-lg font-semibold mt-1">
              {rangeStart ? format(rangeStart, "MMM d, yyyy") : "Add date"}
            </p>
          </div>

          <div className="w-px bg-border self-stretch" />

          <div className="text-center">
            <p className="text-xs font-semibold uppercase">Checkout</p>
            <p className="text-lg font-semibold mt-1">
              {rangeEnd ? format(rangeEnd, "MMM d, yyyy") : "Add date"}
            </p>
          </div>
        </div>

        {/* Calendars */}
        <div className="flex items-start gap-8 relative">
          <button
            onClick={() => {
              const today = new Date();
              const firstDayCurrentMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1,
              );

              if (currentMonth > firstDayCurrentMonth) {
                setCurrentMonth(subMonths(currentMonth, 1));
              }
            }}
            disabled={
              currentMonth <=
              new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            }
            className="p-1 rounded-full hover:bg-primary hover:text-white disabled:opacity-30 disabled:cursor-not-allowed absolute left-5"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex gap-8 flex-1">
            <div className="w-full md:w-1/2">
              <MonthGrid
                month={currentMonth}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                onDayClick={handleDayClick}
              />
            </div>

            <div className="hidden md:block w-1/2">
              <MonthGrid
                month={nextMonth}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                onDayClick={handleDayClick}
              />
            </div>
          </div>


          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-1 rounded-full hover:bg-primary hover:text-white absolute right-5"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Flexibility chips */}
        {
          <div className="flex gap-2 mt-6 flex-wrap justify-center">
            {flexOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFlexOption(opt.value)}
                className={`
                  px-4 py-2 text-sm rounded-full border transition-all
                  ${
                    flexOption === opt.value
                      ? "bg-primary text-white shadow"
                      : "border-gray-300 hover:border-black"
                  }
                `}
              >
                {opt.label}
              </button>
            ))}
          </div>
        }
      </>
    </div>
  );
}
